import React, {
  createContext,
  useContext,
  ReactNode,
  useState,
  useEffect,
} from 'react';
import Classroom from 'models/Classroom';
import useApiCall from './ApiCall';
import userService from 'services/userService';
import { useUserContext } from './UserContext';

interface ClassroomContextType {
  classrooms: Classroom[];
  setClassrooms: React.Dispatch<React.SetStateAction<Classroom[]>>;
}

const ClassroomContext = createContext<ClassroomContextType | undefined>(
  undefined,
);

export function useUserClassrooms() {
  const context = useContext(ClassroomContext);
  if (context === undefined) {
    throw new Error(
      'useUserClassrooms must be used within a UserClassroomsProvider',
    );
  }
  return context;
}

interface UserClassroomsProviderProps {
  children: ReactNode;
}

export function UserClassroomsProvider({
  children,
}: UserClassroomsProviderProps) {
  const [classrooms, setClassrooms] = useState<Classroom[]>([]);
  const { user } = useUserContext();
  const makeApiCall = useApiCall();

  useEffect(() => {
    if (
      !user?.id ||
      !['Teacher', 'Student', 'Admin'].includes(user?.account_type)
    )
      return;
    makeApiCall(userService.getUserClassrooms, user.id)
      .then((respClassrooms) => setClassrooms(respClassrooms))
      .catch((error) => alert(error.message));
  }, [user?.id, user?.account_type, makeApiCall]);

  return (
    <ClassroomContext.Provider value={{ classrooms, setClassrooms }}>
      {children}
    </ClassroomContext.Provider>
  );
}
